import {
  FC,
  createContext,
  PropsWithChildren,
  useContext,
  useMemo
} from 'react'

import usePopup, { PopupReturnTypeWithPayload } from 'hooks/usePopup'
import {
  CompleteRebatesPaymentPopupPayload,
  DisputeProcessCantBeDonePopupPayload,
  InitiateRebatesPaymentPopupPayload,
  InvoicesDetailsPopupPayload,
  RebatesPaymentPopupPayload,
  completeRebatesPaymentPopupWithPayload,
  disputeProcessCantBeDonePopupWithPayload,
  initiateRebatesPaymentPopupWithPayload,
  invoicesDetailsPopupWithPayload,
  rebatesPaymentPopupWithPayload
} from './constants'

type ContextProps = {
  confirmRebatesPaymentPopup: PopupReturnTypeWithPayload<RebatesPaymentPopupPayload>
  completeRebatesPaymentPopup: PopupReturnTypeWithPayload<CompleteRebatesPaymentPopupPayload>
  initiateRebatesPaymentPopup: PopupReturnTypeWithPayload<InitiateRebatesPaymentPopupPayload>
  invoicesDetailsPopup: PopupReturnTypeWithPayload<InvoicesDetailsPopupPayload>
  disputeProcessCantBeDonePopup: PopupReturnTypeWithPayload<DisputeProcessCantBeDonePopupPayload>
}

const basePopup = (title: string) => ({
  title
})

const FinancialsPopupContext = createContext<ContextProps>({
  confirmRebatesPaymentPopup: rebatesPaymentPopupWithPayload,
  completeRebatesPaymentPopup: completeRebatesPaymentPopupWithPayload,
  initiateRebatesPaymentPopup: initiateRebatesPaymentPopupWithPayload,
  invoicesDetailsPopup: invoicesDetailsPopupWithPayload,
  disputeProcessCantBeDonePopup: disputeProcessCantBeDonePopupWithPayload
})

const FinancialsPopupProvider: FC<PropsWithChildren> = ({ children }) => {
  const confirmRebatesPaymentPopupProps = usePopup<RebatesPaymentPopupPayload>(
    basePopup('Pay Rebates')
  )
  const completeRebatesPaymentPopupProps =
    usePopup<CompleteRebatesPaymentPopupPayload>(basePopup('Complete Rebates'))
  const initiateRebatesPaymentPopupProps =
    usePopup<InitiateRebatesPaymentPopupPayload>(basePopup('Pay Rebates'))
  const invoicesDetailsPopupProps = usePopup<InvoicesDetailsPopupPayload>(
    basePopup('See invoice details')
  )
  const disputeProcessCantBeDonePopupProps =
    usePopup<DisputeProcessCantBeDonePopupPayload>()

  const context = useMemo(
    () => ({
      confirmRebatesPaymentPopup: confirmRebatesPaymentPopupProps,
      completeRebatesPaymentPopup: completeRebatesPaymentPopupProps,
      initiateRebatesPaymentPopup: initiateRebatesPaymentPopupProps,
      invoicesDetailsPopup: invoicesDetailsPopupProps,
      disputeProcessCantBeDonePopup: disputeProcessCantBeDonePopupProps
    }),
    [
      initiateRebatesPaymentPopupProps,
      confirmRebatesPaymentPopupProps,
      completeRebatesPaymentPopupProps,
      invoicesDetailsPopupProps,
      disputeProcessCantBeDonePopupProps
    ]
  )

  return (
    <FinancialsPopupContext.Provider value={context}>
      {children}
    </FinancialsPopupContext.Provider>
  )
}

export const useFinancialsPopup = () => useContext(FinancialsPopupContext)

export default FinancialsPopupProvider
